import { IExtremeGridState } from '../entities';
import {
	BCExtremeGridHelpers,
	BCExtremeGridConsts,
	BCExtremeGridEntities
} from '@bimser/components';
import { isNullOrUndefined, MLHelper } from '@bimser/core';
import {
	ILoadSortOptions,
	SortOrder,
	ILoadFilterOptions,
	ILoadFilterItem,
	FilterType,
	ItemType,
	GroupOperator,
	IExtremeGridLoadOptions
} from '../entities/api';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);

export default function getLoadOptions(gridState: IExtremeGridState) {
	if (gridState) {
		const sorts = getSorts(gridState);
		const pagination = getPagination(gridState);
		const filters = getFilters(gridState);

		const loadOptions: IExtremeGridLoadOptions = {
			sorts: sorts,
			pagination: pagination,
			filters: filters
		};

		return loadOptions;
	}
}

function getSorts(gridState: IExtremeGridState) {
	const sortingColumns = BCExtremeGridHelpers.getSortingColumns(gridState.columns);
	const sorts: Array<ILoadSortOptions> = sortingColumns.map(item => ({
		Column: item.name,
		Type: item.sortOrder === 'desc' ? SortOrder.Desc : SortOrder.Asc
	}));

	return sorts;
}

function getPagination(gridState: IExtremeGridState) {
	const pagination = {
		skip: 0,
		take: BCExtremeGridConsts.defaultPageSize
	};
	if (gridState.paging) {
		const pageSize = gridState.paging.pageSize || BCExtremeGridConsts.defaultPageSize;
		const pageIndex =
			gridState.paging.pageIndex !== null && gridState.paging.pageIndex !== undefined
				? gridState.paging.pageIndex
				: 0;

		pagination.skip = pageIndex * pageSize;
		pagination.take = pageSize;
	}

	return pagination;
}

function getFilterValue(filter: any, dataType: any) {
	let value = filter.value;
	if (filter.value && dayjs(filter.value).isValid()) {
		const date = new Date(filter.value);
		if (['datetime', 'datetimeOffset'].includes(dataType)) {
			switch (filter.operation) {
				case 'equal':
				case 'lessThan':
				case 'graterThenOrEqual':
					value = dayjs(new Date(date.setSeconds(0))).format();
					break;
				case 'greaterThan':
				case 'lessThanOrEqual':
					value = dayjs(new Date(date.setSeconds(59))).format();
					break;
			}
		} else if (dataType == 'date') {
			date.setHours(0, 0, 0, 0);
			value = date;
		}
	}

	return value;
}

function getFilters(gridState: IExtremeGridState) {
	const filters: ILoadFilterOptions = [];

	const filteredColumns = BCExtremeGridHelpers.getFilters(gridState.columns);

	const filterItems: ILoadFilterOptions = filteredColumns.reduce((result, item) => {
		if (!isNullOrUndefined(item.value) && item.value !== '') {
			const column = gridState.columns.find(c => c.name === item.columnName);

			if (
				['datetime', 'datetimeOffset'].includes(column.dataType) &&
				item.operation == 'equal'
			) {
				result.push({
					Column: item.columnName,
					Not: item.operation === 'notContains' || item.operation === 'notEqual',
					Type: getFilterType(item.operation),
					Value: getFilterValue(item, column.dataType),
					ItemType: getItemType(column.dataType || 'string')
				});
			} else {
				result.push({
					Column: item.columnName,
					Not: item.operation === 'notContains' || item.operation === 'notEqual',
					Type: getFilterType(item.operation),
					Value: getFilterValue(item, column.dataType),
					ItemType: getItemType(column.dataType || 'string')
				});
			}
		}

		return result;
	}, []);

	if (!isNullOrUndefined(gridState.searching?.value) && gridState.searching?.value !== '') {
		const searchItems: Array<ILoadFilterItem> = gridState.columns.reduce((result, item) => {
			const value = gridState?.searching?.value;
			if (
				item.searchingEnabled !== false &&
				(item.dataType === 'string' || item.dataType === 'multiLanguage')
			) {
				result.push({
					Column: item.name,
					Not: false,
					Type: FilterType.Contain,
					Value: value,
					ItemType: ItemType.String
				});
			} else if (
				item.searchingEnabled !== false &&
				item.dataType == 'number' &&
				!isNaN(Number(value))
			) {
				result.push({
					Column: item.name,
					ItemType: ItemType.Decimal,
					Not: false,
					Type: FilterType.Equal,
					Value: Number(value)
				});
			}

			return result;
		}, []);

		if (searchItems.length > 0) {
			filters.push({
				Operator: GroupOperator.Or,
				Filters: searchItems
			});
		}
	}

	if (filterItems.length > 0) {
		filters.push({
			Operator: GroupOperator.And,
			Filters: filterItems
		});
	}

	return filters;
}

function getFilterType(operation: BCExtremeGridEntities.FilterOperations): FilterType {
	switch (operation) {
		case 'contains':
			return FilterType.Contain;
		case 'endsWith':
			return FilterType.EndsWith;
		case 'equal':
			return FilterType.Equal;
		case 'graterThenOrEqual':
			return FilterType.GreaterOrEqual;
		case 'greaterThan':
			return FilterType.Greater;
		case 'lessThan':
			return FilterType.Less;
		case 'lessThanOrEqual':
			return FilterType.LessOrEqual;
		case 'notContains':
			return FilterType.Contain;
		case 'notEqual':
			return FilterType.Equal;
		case 'startsWith':
			return FilterType.StartsWith;
		default:
			return FilterType.Contain;
	}
}

function getItemType(dataType: BCExtremeGridEntities.DataTypes): ItemType {
	switch (dataType) {
		case 'boolean':
			return ItemType.Boolean;
		case 'date':
			return ItemType.DateTime;
		case 'datetime':
			return ItemType.DateTime;
		case 'datetimeOffset':
			return ItemType.DateTimeOffset;
		case 'number':
			return ItemType.Decimal;
		case 'string':
			return ItemType.String;
		case 'time':
			return ItemType.DateTime;
		default:
			return ItemType.String;
	}
}
